import React from 'react'
import { Checkbox, MultiSelect, Select, TextInput } from '@mantine/core'
import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import includes from 'lodash/includes'
import union from 'lodash/union'
import without from 'lodash/without'

import {
  CalendarCourtReporterAdminEvent,
  CalendarCourtReporterEvent,
  CourtReporter,
  EventFormat,
} from '@/apiClient'
import { ReporterSelect } from '@/components/ReporterSelect'
import { StateMultiSelect } from '@/components/StateMultiSelect'
import { Event, ReporterBase, ReporterEvent } from '@/types'
import { TIMEZONES } from '@/utils/timezones'

export const ReporterStatusFilter: React.FC<{
  values: ReporterEvent['reporter_status'][]
  onChange: (values: ReporterEvent['reporter_status'][]) => void
}> = ({ values, onChange }) => {
  const statuses: {
    values: ReporterEvent['reporter_status'][]
    label: string
    color: string
  }[] = [
    { values: ['available'], label: 'Available', color: 'green' },
    { values: ['booked'], label: 'My Bookings', color: 'indigo' },
    { values: ['waiting'], label: 'Waiting for action', color: 'red' },
    { values: ['past'], label: 'Past', color: 'gray' },
    { values: ['cancelled'], label: 'My canceled', color: 'gray' },
    {
      values: ['overlapping', 'overlapping_related'],
      label: 'Overlapping',
      color: 'yellow',
    },
  ]

  return (
    <>
      {statuses.map(status => (
        <Checkbox
          key={status.values[0]}
          color={status.color}
          label={status.label}
          checked={includes(values, status.values[0])}
          onChange={e =>
            onChange(
              e.currentTarget.checked
                ? union(values, status.values)
                : without(values, ...status.values),
            )
          }
        />
      ))}
    </>
  )
}

export const GeneralStatusFilter: React.FC<{
  values: Event['general_status'][]
  onChange: (values: Event['general_status'][]) => void
}> = ({ values, onChange }) => {
  const statuses: {
    value: Event['general_status']
    label: string
    color: string
  }[] = [
    { value: 'available', label: 'Available', color: 'green' },
    { value: 'booked', label: 'Booked', color: 'indigo' },
    { value: 'waiting', label: 'Waiting for action', color: 'red' },
    { value: 'past', label: 'Past', color: 'gray' },
    { value: 'cancelled', label: 'Canceled', color: 'gray' },
  ]

  return (
    <>
      {statuses.map(status => (
        <Checkbox
          key={status.value}
          color={status.color}
          label={status.label}
          checked={includes(values, status.value)}
          onChange={e =>
            onChange(
              e.currentTarget.checked
                ? union(values, [status.value])
                : without(values, status.value),
            )
          }
        />
      ))}
    </>
  )
}

export const CourtReporterStatusFilter: React.FC<{
  values: CalendarCourtReporterAdminEvent['general_status'][]
  onChange: (
    values: CalendarCourtReporterAdminEvent['general_status'][],
  ) => void
}> = ({ values, onChange }) => {
  const statuses: {
    value: CalendarCourtReporterAdminEvent['general_status']
    label: string
    color: string
  }[] = [
    { value: 'available', label: 'Available', color: 'green' },
    { value: 'requested', label: 'Requested', color: 'indigo' },
    { value: 'booked', label: 'Booked', color: 'indigo' },
    { value: 'past', label: 'Past', color: 'gray' },
    { value: 'cancelled', label: 'Canceled', color: 'gray' },
    { value: 'unavailable', label: 'Unavailable', color: 'dark' },
  ]

  return (
    <>
      {statuses.map(status => (
        <Checkbox
          key={status.value}
          color={status.color}
          label={status.label}
          checked={includes(values, status.value)}
          onChange={e =>
            onChange(
              e.currentTarget.checked
                ? union(values, [status.value])
                : without(values, status.value),
            )
          }
        />
      ))}
    </>
  )
}

export const CourtReporterOwnStatusFilter: React.FC<{
  values: CalendarCourtReporterEvent['court_reporter_status'][]
  onChange: (
    values: CalendarCourtReporterEvent['court_reporter_status'][],
  ) => void
}> = ({ values, onChange }) => {
  const statuses: {
    value: CalendarCourtReporterEvent['court_reporter_status']
    label: string
    color: string
  }[] = [
    { value: 'booked', label: 'My Bookings', color: 'indigo' },
    { value: 'past', label: 'Past', color: 'gray' },
    { value: 'cancelled', label: 'My canceled', color: 'gray' },
  ]

  return (
    <>
      {statuses.map(status => (
        <Checkbox
          key={status.value}
          color={status.color}
          label={status.label}
          checked={includes(values, status.value)}
          onChange={e =>
            onChange(
              e.currentTarget.checked
                ? union(values, [status.value])
                : without(values, status.value),
            )
          }
        />
      ))}
    </>
  )
}

export const CourtReporterFilter: React.FC<{
  courtReporters: CourtReporter[]
  value?: CourtReporter
  onChange: (value?: CourtReporter) => void
}> = ({ courtReporters, value, onChange }) => (
  <Select
    data={courtReporters.map(courtReporter => ({
      value: courtReporter.id,
      label: courtReporter.name,
    }))}
    placeholder="All court reporters"
    clearable
    searchable
    value={value?.id ?? null}
    onChange={id => onChange(courtReporters.find(cr => cr.id === id))}
  />
)

export const EventFormatFilter: React.FC<{
  value?: EventFormat
  onChange: (value?: EventFormat) => void
}> = ({ value, onChange }) => (
  <Select
    data={[
      { value: EventFormat.on_site, label: 'In person' },
      { value: EventFormat.remote, label: 'Remote' },
    ]}
    placeholder="In person or remote"
    clearable
    value={value ?? null}
    onChange={value => onChange((value as EventFormat | null) ?? undefined)}
  />
)

export const ReporterFilter: React.FC<{
  reporters: ReporterBase[]
  value?: ReporterBase
  onChange: (value?: ReporterBase) => void
}> = ({ reporters, value, onChange }) => (
  <ReporterSelect
    value={value}
    reporters={reporters}
    onChange={onChange}
    placeholder="All reporters"
    clearable
    searchable
  />
)

export const StateFilter: React.FC<{
  value?: string[]
  onChange: (value: string[]) => void
}> = ({ value, onChange }) => (
  <StateMultiSelect
    value={value}
    onChange={onChange}
    placeholder="All states"
    clearable
  />
)

export const TimezoneFilter: React.FC<{
  value?: string[]
  onChange: (value: string[]) => void
}> = ({ value = [], onChange }) => (
  <MultiSelect
    data={TIMEZONES}
    placeholder="All timezones"
    clearable
    value={value}
    onChange={onChange}
  />
)

export const SearchFilter: React.FC<{
  value?: string
  placeholder?: string
  onChange: (value: string) => void
}> = ({ value, placeholder = 'Search', onChange }) => (
  <TextInput
    rightSection={<MagnifyingGlassIcon color="gray" />}
    placeholder={placeholder}
    value={value ?? ''}
    onKeyDown={e => {
      // we stop here so the keystrokes are not caught
      // by `useHotKeys` which sits above in the tree
      e.stopPropagation()
    }}
    onChange={e => onChange(e.target.value)}
  />
)

// `value = []` keeps the MultiSelect controlled, as `StateMultiSelect` does.
// Passing `undefined` through flips mantine to uncontrolled and leaves the
// previously selected chips on screen after the filter has been cleared.
export const EventLevelFilter: React.FC<{
  levels: { label: string; value: string }[]
  placeholder: string
  value?: number[]
  maxLevel?: number
  onChange: (value?: number[]) => void
}> = ({ levels, placeholder, value = [], maxLevel, onChange }) => (
  <MultiSelect
    data={levels.map(level => ({
      ...level,
      disabled: maxLevel !== undefined && parseInt(level.value) > maxLevel,
    }))}
    placeholder={placeholder}
    clearable
    searchable
    value={value.map(level => level.toString())}
    onChange={value =>
      onChange(
        value !== undefined ? value.map(level => parseInt(level)) : undefined,
      )
    }
  />
)

export const BookedByReporterFromDifferentStateFilter: React.FC<{
  value?: boolean
  onChange: (value?: boolean) => void
}> = ({ value, onChange }) => (
  <Select
    data={[
      { value: 'true', label: 'With OOS' },
      { value: 'false', label: 'Without OOS' },
    ]}
    placeholder="With or without OOS"
    clearable
    value={value?.toString() ?? null}
    onChange={value => onChange(value ? value === 'true' : undefined)}
  />
)
